//#region Imports Packages and Libs

import { store } from "../../store/store";
import { TranscriptContent, TranscriptRequestPayload } from "../../types/components/transcript";
import { StringUtils } from "../../helpers/string_utils";
import { Util } from "../../util";

//#endregion

//#region Transcript Request Builder Class

/**
 * Class responsible for building transcript request payloads
 */
export class TranscriptRequestBuilder {
	//#region Public Methods

	/**
	 * Creates a payload for the transcript API request with a nested Content structure
	 *
	 * @param {string} primaryEmail - The primary recipient email address
	 * @param {string[]} additionalEmails - Array of additional email addresses
	 * @param {string} note - Optional note to include with the transcript
	 * @returns {TranscriptRequestPayload} - The prepared request payload with nested Content object
	 */
	public static buildPayload(primaryEmail: string, additionalEmails: string[], note: string): TranscriptRequestPayload {
		// Create the payload object with required fields and nested Content object
		// using the store to get company ID, bot ID, user ID and conversation ID

		// Create Transcript Content data first
		const transcriptContent: TranscriptContent = {
			toEmail: this.formatPrimaryEmail(primaryEmail),
			shareWith: this.formatAdditionalEmails(additionalEmails),
			note: this.formatNote(note),
		};

		// Construct the main payload object with the Content and other required fields
		const payload: TranscriptRequestPayload = {
			Content: transcriptContent,
			CompanyId: StringUtils.getStoreValue(store.state, "companyId", 0), // store.state.companyId is a number
			BotId: StringUtils.getStoreValue(store.state, "extBotId", 0), // store.state.extBotId is a number
			UserId: StringUtils.getStoreValue(store.state, "userId", ""), // store.state.userId is a string
			ConvId: StringUtils.getStoreValue(store.state, "convId", ""), // store.state.convId is a string
			SendMessageUrl: StringUtils.getStoreValue(store.state, "sendMsgUrl", ""), // store.state.sendMsgUrl
		};

		// Return the constructed payload
		return payload;
	}

	//#endregion

	//#region Private Methods

	/**
	 * Formats the primary email address
	 * @param {string} email - The primary email address
	 * @returns {string} - The formatted email
	 */
	private static formatPrimaryEmail(email: string): string {
		return email ? Util.trimString(email) : "";
	}

	/**
	 * Formats the additional email addresses as a comma-separated string
	 * @param {string[]} emails - Array of additional email addresses
	 * @returns {string} - Comma-separated email addresses
	 */
	private static formatAdditionalEmails(emails: string[]): string {
		if (!Array.isArray(emails) || emails.length === 0) {
			return "";
		}

		return StringUtils.joinStrings(emails.map((email) => Util.trimString(email)).filter((email) => !!email));
	}

	/**
	 * Formats the optional note text
	 * @param {string} note - The note text
	 * @returns {string} - The formatted note
	 */
	private static formatNote(note: string): string {
		return note ? Util.trimString(note) : "";
	}

	//#endregion
}

//#endregion
